import { useEffect, useCallback, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import CloseIcon from 'assets/images/shopping/closeIcon';
import CopyIcon from 'assets/images/shopping/CopyIcon';
import useLockScroll from 'hooks/useLockScroll';
import { IShipmentData } from 'types/orders';
import { getOrderDetailedData } from 'services/orders';
import GenericLoader from 'components/GenericLoader/GenericLoader';
import { copyTextToClipboard, getDestinationDisplayName, groupProductsBySku } from '../../trackingOrder.utils';
import PreparationProductRow from './PreparationProductRow';
import './PreparationChecklistDrawer.scss';

interface IPreparationChecklistDrawer {
  isOpen: boolean;
  onClose: () => void;
  orderId?: number | string;
  shipmentId?: number;
}

/** ID del contenedor donde se renderizará el drawer mediante Portal */
const PORTAL_CONTAINER_ID = 'ordersListingOperations';

/** Lista de tareas del checklist de preparación */
const PREPARATION_CHECKLIST_ITEMS = [ // TODO: Integrar cuando el backend esté listo
  { id: 1, label: 'Teléfono y dirección del destino validados.' },
  { id: 2, label: 'Compra de stock y precios confirmados con el proveedor.' },
  { id: 3, label: 'Orden de compra cargada para que Finanzas procese el pago.' },
  { id: 4, label: 'Comprobante de pago enviado al proveedor.' }
];

/**
 * Modal drawer que se desliza desde la derecha mostrando el checklist
 * de preparación de un envío con lista de tareas y productos.
 */
const PreparationChecklistDrawer = ({
  isOpen,
  onClose,
  orderId,
  shipmentId,
}: IPreparationChecklistDrawer) => {
  const [shipmentData, setShipmentData] = useState<IShipmentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});

  const destinationTitle = getDestinationDisplayName(
    shipmentData?.place,
    shipmentData?.receiverName || shipmentData?.receiverInfo?.receiverName,
    shipmentData?.country?.name
  );

  const products = shipmentData?.products || [];
  const groupedProducts = groupProductsBySku(products);
  const referencesCount = Object.keys(groupedProducts).length;

  /**
   * Obtiene los detalles del envío desde el endpoint de detalle de orden.
   */
  const fetchShipmentDetail = useCallback(async () => {
    if (!orderId || !shipmentId) return;

    setIsLoading(true);
    setHasError(false);

    try {
      const orderData = await getOrderDetailedData(orderId);
      const shipments = orderData?.generalData?.shipmentData || [];
      const foundShipment = shipments.find(s => s.shipmentId === shipmentId);

      if (foundShipment) {
        setShipmentData(foundShipment);
      } else {
        setHasError(true);
        setShipmentData(null);
      }
    } catch {
      setHasError(true);
      setShipmentData(null);
    } finally {
      setIsLoading(false);
    }
  }, [orderId, shipmentId]);

  const handleCheckboxChange = (itemId: number) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  useEffect(() => {
    if (isOpen && orderId && shipmentId) {
      fetchShipmentDetail();
    }
  }, [isOpen, orderId, shipmentId, fetchShipmentDetail]);

  const additionalElementsToLock = useMemo(() => [PORTAL_CONTAINER_ID], []);
  useLockScroll(isOpen, additionalElementsToLock);

  const resetShipmentState = () => {
    setShipmentData(null);
    setHasError(false);
    setCheckedItems({});
  };
  
  useEffect(() => {
    if (!isOpen) {
      resetShipmentState();
    }
  }, [isOpen]);
  

  const portalContainer = document.getElementById(PORTAL_CONTAINER_ID);

  if (!isOpen) return null;

  const drawerContent = (
    <>
      <div className="preparationChecklistDrawerOverlay" onClick={onClose} />

      <div className="preparationChecklistDrawer">
        <header className="drawerHeader">
          <h2 className="drawerTitle">
            Checklist de Preparación ({referencesCount})
          </h2>
          <button className="closeButton" onClick={onClose} aria-label="Cerrar modal">
            <CloseIcon />
          </button>
        </header>

        <div className="drawerContent">
          {isLoading && (
            <div className="loadingState">
              <GenericLoader sizeLoader={60} />
            </div>
          )}

          {hasError && !isLoading && (
            <div className="errorState">
              <span>Error al cargar el detalle del envío</span>
              <button className="retryButton" onClick={fetchShipmentDetail}>
                Reintentar
              </button>
            </div>
          )}

          {!isLoading && !hasError && shipmentData && (
            <>
              <div className="shipmentHeader">
                <span className="destinationTitle">
                  Envío {destinationTitle}
                  <button
                    className="copyButton"
                    onClick={() => copyTextToClipboard(destinationTitle)}
                    aria-label="Copiar destino"
                  >
                    <CopyIcon />
                  </button>
                </span>
                <Link to={`/order/details/${orderId}`} className="orderLink">
                  Orden N°{orderId}
                </Link>
              </div>

              <div className="checklistSection">
                {PREPARATION_CHECKLIST_ITEMS.map(item => (
                  <label key={item.id} className="checklistItem">
                    <input
                      type="checkbox"
                      checked={checkedItems[item.id] || false}
                      onChange={() => handleCheckboxChange(item.id)}
                      className="checklistCheckbox"
                    />
                    <span className="checklistLabel">{item.label}</span>
                  </label>
                ))}
              </div>

              <div className="productsSection">
                <div className="productsSectionHeader">
                  <span className="productsTitle">Productos</span>
                  <span className="referencesCount">
                    {referencesCount} Referencia{referencesCount > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="productsList">
                  {Object.entries(groupedProducts).map(([sku, skuProducts]) => (
                    <PreparationProductRow key={sku} product={skuProducts[0]} quantity={skuProducts.length} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );

  if (portalContainer) {
    return createPortal(drawerContent, portalContainer);
  }

  return drawerContent;
};

export default PreparationChecklistDrawer;

